/**
 * ValueChecklist — a controlled, presentational searchable multi-select checklist
 * for ONE categorical field, built for the Portfolio "Segment Builder".
 *
 * The field's full value set is fixed to the total portfolio (`options`), each
 * value carrying its per-value loan `count`. The control lets the user:
 *   - Search/filter the value list (filtered in render via `normalizeSearch`,
 *     the same alphanumeric-strip matcher the mapping combobox uses — no debounce).
 *   - Check individual values; the list is VIRTUALIZED (TanStack Virtual, same as
 *     DataTable) so it stays smooth at 5,000+ options and degrades gracefully for
 *     few-value fields.
 *   - Select all / Select none across the CURRENT search-filtered set (see below).
 *   - Include blank/missing loans (`includeBlank`).
 *
 * TRUNCATION (F122): `options` is the field's TOP values (capped server-side at 200). When a
 * field has more distinct values than that (`distinctCount > options.length`), the local list
 * cannot see the tail — searching it would show "no matches" for a real value and "Select all"
 * would silently drop the tail. So when TRUNCATED and a `serverSearch` adapter is supplied:
 *   - a header states "showing top N of M values",
 *   - the search box queries the WHOLE distinct set server-side (via `onServerSearch`), and the
 *     rendered results come from `serverSearch.options` (not the local 200), so a tail value is
 *     findable and selectable,
 *   - "Select all" resolves the EXACT matching set server-side (`onSelectAllMatching`); when the
 *     match is too large to enumerate (`serverSearch.hasMore`) it is disabled with a "narrow your
 *     search" nudge — NEVER an IN() built from the visible 200.
 * Without the adapter (few-value fields / stories), the component behaves exactly as before.
 *
 * INCLUSION-ONLY: the emitted filter always means "keep loans whose value is
 * checked" — the checklist already expresses inclusion, so there is no
 * Include/Exclude toggle. (The wire schema still carries a `mode`; the Segment
 * Builder hardcodes `include` when it emits.)
 *
 * Pure presentational + fully controlled: it renders from props and emits the
 * complete next value via a single `onChange({ values, includeBlank })`.
 * No internal selection state — only the transient search query is local.
 */
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '~/components/ui';
import styles from './ValueChecklist.module.css';

/** One option: a categorical value and its per-value loan count. */
export interface ValueChecklistOption {
  readonly value: string;
  readonly count: number;
}

/**
 * F122 server-search adapter for a TRUNCATED field: the parent runs the field-values query
 * keyed on the debounced search term and feeds back the whole-portfolio matches here.
 */
export interface ValueChecklistServerSearch {
  /** Distinct values matching the current query across the WHOLE portfolio (the server page). */
  readonly options: readonly ValueChecklistOption[];
  /** True while the query is in flight. */
  readonly loading: boolean;
  /** True when more matches exist than the page returned — an exact "Select all" is impossible,
   *  so the user must narrow the search (never a truncated enumeration). */
  readonly hasMore: boolean;
}

/** A rendered row in the virtualized list: an option, a group header, or a note. */
type ChecklistEntry =
  | { readonly kind: 'header'; readonly label: string }
  | { readonly kind: 'note'; readonly text: string }
  | { readonly kind: 'option'; readonly option: ValueChecklistOption };

export interface ValueChecklistProps {
  /** The field's display name (e.g. "Loan Status"). */
  readonly label: string;
  /** Every value for this field across the total portfolio, with loan counts. */
  readonly options: readonly ValueChecklistOption[];
  /** The currently-selected values (a subset of `options` by value). */
  readonly selected: readonly string[];
  /** Whether loans with a blank/missing value are kept. */
  readonly includeBlank: boolean;
  /** Emit the FULL next filter value (controlled — never mutate props). */
  readonly onChange: (next: {
    values: string[];
    includeBlank: boolean;
  }) => void;
  /** Disable all interaction (e.g. the field is inactive). */
  readonly disabled?: boolean;
  /**
   * Embedded in a host surface (the SegmentPanel's white body): drop this
   * control's own card chrome (border / bg / shadow / radius) and suppress the
   * internal label header so it doesn't nest a second card / repeat the field
   * name. Standalone (stories) leave it off.
   */
  readonly embedded?: boolean;
  /**
   * F122 — the field's TOTAL distinct-value count across the portfolio. When it exceeds
   * `options.length`, the list is truncated (top-N) and the honesty label + server search engage.
   * Null/undefined (or ≤ options.length) ⇒ the list is complete; no truncation UI.
   */
  readonly distinctCount?: number | null;
  /**
   * F122 — called (debounced) with the search query when the field is TRUNCATED, so the parent
   * can query the whole distinct set server-side. Omit for non-truncated / standalone use.
   */
  readonly onServerSearch?: (query: string) => void;
  /** F122 — the server-search results for the current query (truncated fields only). */
  readonly serverSearch?: ValueChecklistServerSearch | null;
  /**
   * F122 — resolve "Select all" for a truncated field server-side: the parent fetches the FULL
   * matching set and unions it into the selection. Receives the current search query. Only wired
   * for truncated fields; when the match is unbounded the button is disabled instead of called.
   */
  readonly onSelectAllMatching?: (query: string) => void;
}

/** Debounce (ms) before a truncated field's search box hits the server. */
const SERVER_SEARCH_DEBOUNCE_MS = 250;

/** Uniform row height (px) — uniform rows keep virtualization measurement-free. */
const ROW_HEIGHT = 36;
/** Group-header row height (px) — the "Search Results" / "Enabled Values" labels. */
const HEADER_HEIGHT = 30;
/** Cap the visible scroll viewport so a large value set scrolls inside the control. */
const MAX_LIST_HEIGHT = 280;

/**
 * Strip non-alphanumerics + lowercase both sides so search is punctuation- and
 * case-insensitive (lifted from the mapping ColumnTargetCombobox matcher). An
 * empty query matches everything.
 */
function normalizeSearch(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Coalesce a CSS-module class lookup (typed `string | undefined`) to a string. */
function cx(...classes: (string | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Searchable, virtualized multi-select value checklist. */
export function ValueChecklist({
  label,
  options,
  selected,
  includeBlank,
  onChange,
  disabled = false,
  embedded = false,
  distinctCount = null,
  onServerSearch,
  serverSearch = null,
  onSelectAllMatching,
}: ValueChecklistProps): ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // F122 — the list is truncated when the field has more distinct values than we were given
  // options for AND the parent supplied a server-search adapter to reach the tail.
  const truncated =
    onServerSearch !== undefined &&
    distinctCount !== null &&
    distinctCount !== undefined &&
    distinctCount > options.length;

  // Debounce the search term out to the server when truncated, so a tail value is findable.
  useEffect(() => {
    if (!truncated || onServerSearch === undefined) return;
    const query = search.trim();
    const id = setTimeout(() => onServerSearch(query), SERVER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [truncated, search, onServerSearch]);

  // Stable Set for O(1) checked lookups while rendering rows.
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // The current search-filtered option set (drives Select all/none). For a truncated field the
  // matches come from the SERVER (the whole distinct set); otherwise it is the local top-N filter.
  // Empty query returns everything (locally).
  const filtered = useMemo(() => {
    if (truncated) return serverSearch?.options ?? [];
    const normalized = normalizeSearch(search);
    if (normalized === '') return options;
    return options.filter((o) => normalizeSearch(o.value).includes(normalized));
  }, [truncated, serverSearch, options, search]);

  const hasSearch = normalizeSearch(search) !== '';

  // Known count per value (the local top-N plus any server matches) — so a selected value not in
  // the current match set can still render with its count in the "Enabled Values" group.
  const countByValue = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of options) map.set(o.value, o.count);
    for (const o of serverSearch?.options ?? []) map.set(o.value, o.count);
    return map;
  }, [options, serverSearch]);

  // The rendered rows: option rows, plus (while searching) group headers. Searching
  // splits the list into "Search Results" (matches) and "Enabled Values" (selected
  // values the query does NOT match) so an enabled value never hides behind a search.
  // A matched value stays only in the results group — never duplicated.
  const entries = useMemo<ChecklistEntry[]>(() => {
    // Truncated, no search: show the local top-N (the label discloses "top N of M").
    if (!hasSearch) {
      return options.map((option) => ({ kind: 'option', option }));
    }
    const matchSet = new Set(filtered.map((o) => o.value));
    // Selected values the current query does not match — from the local options (non-truncated)
    // or, for a truncated field, from the full selection (synthesize a row so a tail selection
    // can always be unchecked even when it isn't in the current server page).
    const enabledNotMatching: ValueChecklistOption[] = truncated
      ? selected
        .filter((v) => !matchSet.has(v))
        .map((v) => ({ value: v, count: countByValue.get(v) ?? 0 }))
      : options.filter((o) => selectedSet.has(o.value) && !matchSet.has(o.value));
    const rows: ChecklistEntry[] = [
      { kind: 'header', label: t('segment.checklist.searchResults') },
    ];
    if (truncated && serverSearch?.loading) {
      rows.push({ kind: 'note', text: t('segment.checklist.searching') });
    } else if (filtered.length === 0) {
      rows.push({ kind: 'note', text: t('segment.checklist.noMatches') });
    } else {
      for (const option of filtered) rows.push({ kind: 'option', option });
      // Disclose that more matches exist than this page shows (F122 — the tail is real).
      if (truncated && serverSearch?.hasMore) {
        rows.push({ kind: 'note', text: t('segment.checklist.moreMatches') });
      }
    }
    if (enabledNotMatching.length > 0) {
      rows.push({ kind: 'header', label: t('segment.checklist.enabledValues') });
      for (const option of enabledNotMatching) rows.push({ kind: 'option', option });
    }
    return rows;
  }, [hasSearch, truncated, options, filtered, selected, selectedSet, serverSearch, countByValue, t]);

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      entries[index]?.kind === 'header' ? HEADER_HEIGHT : ROW_HEIGHT,
    overscan: 12,
  });
  const virtualItems = virtualizer.getVirtualItems();

  // Fixed row heights make overflow deterministic on the first render: the list only
  // becomes a scroll container (height cap + overflow) when its content actually
  // exceeds the cap, so a short list never traps wheel events at a scroll boundary.
  const scrollable = virtualizer.getTotalSize() > MAX_LIST_HEIGHT;

  // Emit the full next value, carrying forward whatever the caller didn't change.
  const emit = (patch: {
    values?: string[];
    includeBlank?: boolean;
  }): void => {
    onChange({
      values: patch.values ?? [...selected],
      includeBlank: patch.includeBlank ?? includeBlank,
    });
  };

  const toggleValue = (value: string, checked: boolean): void => {
    if (checked) {
      if (selectedSet.has(value)) return;
      emit({ values: [...selected, value] });
    } else {
      emit({ values: selected.filter((v) => v !== value) });
    }
  };

  // Select all / none operate on the CURRENT search-filtered set only — not the
  // whole portfolio. "Select all" unions the visible values into the selection;
  // "Select none" removes only the visible values, leaving off-search picks intact.
  // F122: for a TRUNCATED field, "Select all" must resolve the EXACT matching set server-side
  // (via onSelectAllMatching) — never union the visible top-N (which would silently drop the
  // tail). It is disabled when the match is unbounded (hasMore) so the user narrows first.
  const selectAllFiltered = (): void => {
    if (truncated) {
      onSelectAllMatching?.(search.trim());
      return;
    }
    const next = new Set(selected);
    for (const o of filtered) next.add(o.value);
    emit({ values: [...next] });
  };
  const selectNoneFiltered = (): void => {
    const filteredValues = new Set(filtered.map((o) => o.value));
    emit({ values: selected.filter((v) => !filteredValues.has(v)) });
  };

  // F122 — "Select all" can't build an exact set when the server has more matches than one page,
  // so it's disabled with a "narrow your search" nudge rather than enumerating a truncated list.
  const selectAllUnbounded = truncated && (serverSearch?.hasMore ?? false);
  const selectAllDisabled =
    disabled ||
    (truncated
      ? selectAllUnbounded || (serverSearch?.loading ?? false) || onSelectAllMatching === undefined
      : filtered.length === 0);

  const labelId = `value-checklist-${normalizeSearch(label) || 'field'}`;

  return (
    <div
      className={cx(styles.root, embedded ? styles.embedded : undefined)}
      data-testid="value-checklist"
    >
      {embedded ? (
        // Host header already names the field; keep only an off-screen label for
        // the list's aria-labelledby wiring.
        <span id={labelId} className={cx(styles.srOnly)}>
          {label}
        </span>
      ) : (
        <div className={cx(styles.header)}>
          <span id={labelId} className={cx(styles.label)}>
            {label}
          </span>
        </div>
      )}

      <input
        type="search"
        className={cx(styles.search)}
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        placeholder={t('segment.checklist.searchPlaceholder', { label: label.toLowerCase() })}
        aria-label={t('segment.checklist.searchLabel', { label })}
        disabled={disabled}
      />

      {truncated ? (
        // F122 — the list is the top-N; the field has more distinct values than are shown. Say so,
        // and (when searching is idle) point the user at server search to reach the tail.
        <p className={cx(styles.truncationNote)} data-testid="value-checklist-truncation">
          {t('segment.checklist.truncated', {
            shown: options.length,
            total: (distinctCount ?? options.length).toLocaleString('en-US'),
          })}
        </p>
      ) : null}

      <div className={cx(styles.actions)}>
        <button
          type="button"
          className={cx(styles.action)}
          onClick={selectAllFiltered}
          disabled={selectAllDisabled}
          title={selectAllUnbounded ? t('segment.checklist.selectAllNarrow') : undefined}
        >
          {t('segment.checklist.selectAll')}
        </button>
        <span className={cx(styles.actionDivider)} aria-hidden>
          ·
        </span>
        <button
          type="button"
          className={cx(styles.action)}
          onClick={selectNoneFiltered}
          disabled={disabled || filtered.length === 0}
        >
          {t('segment.checklist.selectNone')}
        </button>
        <span className={cx(styles.actionCount)}>
          {t('segment.checklist.selectedCount', { count: selected.length })}
        </span>
      </div>

      {selectAllUnbounded ? (
        <p className={cx(styles.truncationNote)} data-testid="value-checklist-select-all-narrow">
          {t('segment.checklist.selectAllNarrow')}
        </p>
      ) : null}

      <div
        ref={scrollRef}
        className={cx(styles.list, scrollable ? styles.listScrollable : undefined)}
        style={scrollable ? { maxHeight: MAX_LIST_HEIGHT } : undefined}
        role="group"
        aria-labelledby={labelId}
      >
        {entries.length === 0 ? (
          <div className={cx(styles.empty)} data-testid="value-checklist-empty">
            {t('segment.checklist.noMatches')}
          </div>
        ) : (
          <div
            className={cx(styles.listInner)}
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualItems.map((vi) => {
              const entry = entries[vi.index];
              if (!entry) return null;
              const positionStyle = {
                height: vi.size,
                transform: `translateY(${vi.start}px)`,
              };
              if (entry.kind === 'header') {
                return (
                  <div key={vi.key} className={cx(styles.groupHeader)} style={positionStyle}>
                    {entry.label}
                  </div>
                );
              }
              if (entry.kind === 'note') {
                return (
                  <div
                    key={vi.key}
                    className={cx(styles.note)}
                    style={positionStyle}
                    data-testid="value-checklist-empty"
                  >
                    {entry.text}
                  </div>
                );
              }
              const option = entry.option;
              const checked = selectedSet.has(option.value);
              return (
                <div
                  key={option.value}
                  data-index={vi.index}
                  data-testid="value-checklist-row"
                  className={cx(styles.row)}
                  style={positionStyle}
                >
                  <Checkbox
                    classNames={{ root: styles.checkbox ?? '', labelWrapper: styles.checkboxLabelWrap ?? '' }}
                    checked={checked}
                    disabled={disabled}
                    onChange={(event) => toggleValue(option.value, event.currentTarget.checked)}
                    label={<span className={cx(styles.valueText)}>{option.value}</span>}
                  />
                  <span className={cx(styles.count)}>
                    {option.count.toLocaleString('en-US')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={cx(styles.footer)}>
        <Checkbox
          size="sm"
          checked={includeBlank}
          disabled={disabled}
          onChange={(event) => emit({ includeBlank: event.currentTarget.checked })}
          label={t('segment.checklist.includeBlank')}
        />
      </div>
    </div>
  );
}
